import React, { useState } from "react";
import {
    Box,
    Container,
    Link,
    Accordion as MuiAccordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SupportLinksCarousel from "../arrangement/settlement/SupportLinksCarousel";
import RemoveIcon from "@mui/icons-material/Remove";
import ScriptLoader from "../account/ScriptLoader";
import { useClient } from "../../context/ClientProvider";

const UsefulTips = () => {
    const [expanded, setExpanded] = useState(false);
    const { useGetConfig } = useClient();
    const clientConfig = useGetConfig()?.clientConfig;
    const usefulTipsDataPerClient = clientConfig?.layout?.unauthenticatedPages?.usefulTips;
    const contactUsDataPerClient = clientConfig?.layout?.unauthenticatedPages.contactUs;

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const tipItems = [
        {
            key: "creditScoreChecking",
            question: "Check your credit score on a regular basis",
            answer: (
                <>
                    <Typography paragraph>
                        Ok, so your credit score is reliant on lots of factors that the Credit
                        Referencing Agencies manage. Things like having your correct home
                        address on file, or credit financial-health indicators such as how
                        much credit you currently have (the number and the value of your
                        mortgage(s), loan(s), credit card(s), or overdraft(s)).
                    </Typography>
                    <Typography paragraph>
                        The Credit Referencing Agencies are notified of missed
                        payments/repayments, or any defaults from companies lending money to
                        you, as well as organisations like the DVLA – and then certain flags
                        are added to your credit file, based on your borrowing/spending/
                        repayment behavior.
                    </Typography>
                    <Typography paragraph>
                        Depending on how strong your financial health is (i.e. you always make
                        payments on the agreed dates, your income is larger than your
                        outgoings), your credit score will then populate a rating. In essence,
                        the higher the rating is, the more likely you are to be accepted for
                        lending in the future. If you have a poor credit score, don't panic.
                        Get in touch if you'd like support in how to check your credit score.
                    </Typography>
                    <Typography paragraph>
                        You should also consider free, independent advice on how to boost your
                        score and improve your financial health. A lot of banks now offer a
                        credit score check, so have a look in your banking app or call your
                        bank to find out more.
                    </Typography>
                    <Typography paragraph>
                        You can also sign up to a monthly credit score check with companies
                        such as{" "}
                        <Link
                            href="https://www.experian.co.uk/"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                        >
                            Experian
                        </Link>{" "}
                        or{" "}
                        <Link
                            href="https://www.clearscore.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                        >
                            ClearScore
                        </Link>
                        .
                    </Typography>
                </>
            ),
        },
        {
            key: "freeBudgetingTool",
            question: "Use our free Budgeting Tool",
            answer: (
                <>
                    <Typography paragraph>
                        For peace of mind, especially when thinking about loan repayments –
                        we've created a simple Budgeting Tool that will help you see what's
                        coming in vs. what's going out.
                    </Typography>
                    <Typography paragraph>
                        Facing into your finances can feel a bit intimidating, we know – but
                        if you start completing our Budgeting Tool and you need a break at any
                        point, the form will automatically save so you can come back to it and
                        complete it.
                    </Typography>
                    <Typography paragraph>
                        There's only 4 steps to fill in, and once done you'll get a view of
                        the bigger picture of your money. If you're worried about making your
                        loan repayments please get in touch. We can talk you through the
                        Budgeting Tool over the phone and help you out if you get stuck.{" "}
                        Have a go at completing our Budgeting Tool.
                    </Typography>
                </>
            ),
        },
        {
            key: "worriedAboutMoney",
            question: "Worried about money?",
            answer: (
                <>
                    <Typography paragraph>
                        If you're having trouble making your payments, please contact us as
                        soon as possible, so that we can discuss your circumstances and look
                        at ways that we can best support you.
                    </Typography>
                    <Typography paragraph>
                        To explore the options that may be available to you, it would be
                        helpful for you to complete an Income and Expenditure form which
                        breaks down what you earn monthly (income), what you owe and what you
                        spend (expenditure).
                        <Typography paragraph>
                        </Typography>
                        <ul>
                            <li>Arrange a payment plan with you that suits both of us.</li>
                            <li>Change the date you make your payments.</li>
                        </ul>
                    </Typography>
                    <Typography paragraph>
                        It's good to talk. With the cost of living affecting so many people,
                        and money being one the top factors that can affect our mental health
                        — it's important that you get in touch with the team if you're
                        currently feeling the pinch. Call us on{" "}
                        <Link href={`tel:${contactUsDataPerClient?.phoneUs?.phoneNumber}`}>{contactUsDataPerClient?.phoneUs?.phoneNumber}.</Link>
                        , start a conversation using Live Chat, or contact the below
                        organisations for free, impartial advice.
                    </Typography>
                    <SupportLinksCarousel />
                </>
            ),
        },
    ];

    return (
        <>
            <ScriptLoader />
            <Container sx={{ mt: 4, mb: 7 }}>
                <Box sx={{ pb: 2 }}>
                    <Typography
                        variant='h1'
                        fontWeight='bold'
                        fontSize={32}
                        sx={{ py: 4 }}
                    >
                        Useful tips
                    </Typography>
                    <Typography
                        variant='h2'
                        fontWeight='bold'
                        fontSize={25}
                        sx={{ py: 1 }}
                    >
                        Here are some simple steps to check and maintain your financial
                        health
                    </Typography>
                    <Typography sx={{ pt: 1, pb: 1, mb: 3 }}>
                        Most of us are aware of how important it is to look after our health by doing a few basic things like getting enough exercise and eating properly. But how many of us look after our finances in the same way?
                    </Typography>
                    <Typography
                        variant='h2'
                        fontWeight='bold'
                        fontSize={26}
                        sx={{ py: 1 }}
                    >
                        Making sure you're prepared
                    </Typography>
                    <Typography sx={{ pt: 1, pb: 2, mb: 3 }}>
                        The majority of people who find themselves in financial difficulty
                        do so because of unexpected circumstances like being made redundant,
                        reduced income or unexpected expenses.{" "}
                    </Typography>
                    <Typography
                        variant='h4'
                        fontWeight='bold'
                        fontSize={16}
                        sx={{ py: 1 }}
                    >
                        We've listed some tips below that might be useful to you, when
                        managing your finances
                    </Typography>
                </Box>
                {tipItems
                    .filter(item => usefulTipsDataPerClient?.[item.key]?.enabled)
                    .map((item, index) => (
                        <MuiAccordion
                            key={item.key}
                            expanded={expanded === `panel${index}`}
                            onChange={handleChange(`panel${index}`)}
                            sx={{ boxShadow: "none", ":before": { backgroundColor: "transparent" } }}
                        >
                            <AccordionSummary
                                expandIcon={expanded === `panel${index}` ? <RemoveIcon sx={{ color: "#000" }} /> : <AddIcon sx={{ color: "#000" }} />}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                                sx={{ margin: "10px", boxShadow: "none", ":hover": { color: "secondary.main", '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root': { color: "secondary.main" } } }}
                            >
                                <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    "& ul": { pl: 3, listStyle: "disc" },
                                    "& li": { display: "list-item", listStyle: "inherit" },
                                }}
                            >
                                {item.answer}
                            </AccordionDetails>
                        </MuiAccordion>
                    ))}

            </Container>
        </>
    );
};

export default UsefulTips;
